import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BarList,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatTile,
  type BarListItem,
} from '@cartcloser/ui';
import {
  formatMicros,
  formatRate,
  getAnalytics,
  type AnalyticsRange,
  type AnalyticsSummary,
} from '../lib/api.js';
import { useState } from 'react';

export const Route = createFileRoute('/admin/$slug/analytics')({
  component: AnalyticsPage,
});

const RANGES: { value: AnalyticsRange; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

function AnalyticsPage() {
  const { slug } = Route.useParams();
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const query = useQuery({
    queryKey: ['analytics', slug, range],
    queryFn: () => getAnalytics(slug, range),
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-sm text-muted-foreground">
            Live conversation performance from your storefront widget.
          </p>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <Button
              key={r.value}
              size="sm"
              variant={r.value === range ? 'primary' : 'ghost'}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : query.isError || !query.data ? (
        <p className="text-sm text-destructive">Couldn’t load analytics.</p>
      ) : (
        <AnalyticsBody data={query.data} />
      )}
    </div>
  );
}

function AnalyticsBody({ data }: { data: AnalyticsSummary }) {
  const recommended: BarListItem[] = data.topProducts.map((p) => ({
    label: `${p.title} (${p.sku})`,
    value: p.shown,
    meta: `${p.shown} shown`,
  }));
  const clicked: BarListItem[] = [...data.topProducts]
    .filter((p) => p.clicked > 0)
    .sort((a, b) => b.clicked - a.clicked)
    .map((p) => ({ label: `${p.title} (${p.sku})`, value: p.clicked, meta: `${p.clicked} clicks` }));

  const maxDay = Math.max(...data.daily.map((d) => d.conversations), 1);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label="Conversations" value={data.conversations} />
        <StatTile
          label="Engagement"
          value={formatRate(data.engagementRate)}
          sub="reached a recommendation"
        />
        <StatTile
          label="Click-through"
          value={formatRate(data.clickThroughRate)}
          sub={`${data.recsClicked} of ${data.recsShown} shown`}
        />
        <StatTile
          label="Cost"
          value={formatMicros(data.totalCostMicros)}
          sub={`${formatMicros(data.avgCostMicros)} / chat`}
        />
        <StatTile
          label="Satisfaction"
          value={
            data.feedbackUp + data.feedbackDown > 0
              ? formatRate(data.feedbackUp / (data.feedbackUp + data.feedbackDown))
              : '—'
          }
          sub={`👍 ${data.feedbackUp} · 👎 ${data.feedbackDown}`}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Conversations per day</CardTitle>
        </CardHeader>
        <CardContent>
          {data.daily.length === 0 ? (
            <p className="text-sm text-muted-foreground">No conversations in this range yet.</p>
          ) : (
            <div className="flex h-32 items-end gap-1">
              {data.daily.map((d) => (
                <div
                  key={d.dayMs}
                  className="flex-1 rounded-t bg-primary"
                  style={{ height: `${Math.max((d.conversations / maxDay) * 100, 3)}%` }}
                  role="img"
                  aria-label={`${new Date(d.dayMs).toLocaleDateString()}: ${d.conversations} conversations`}
                  title={`${new Date(d.dayMs).toLocaleDateString()}: ${d.conversations}`}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most recommended products</CardTitle>
          </CardHeader>
          <CardContent>
            <BarList items={recommended} emptyLabel="No recommendations in this range yet." />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most clicked products</CardTitle>
          </CardHeader>
          <CardContent>
            <BarList items={clicked} emptyLabel="No clicks in this range yet." />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
